import { Input as MUIInput } from '@mui/material';
import { InputLabel as MUILabel } from '@mui/material';
import classnames from 'classnames';
import {
  ChangeEvent,
  FocusEventHandler,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { DangerIcon } from '@components/Icons';

import styles from './input.module.scss';

export type OnChangeProps =
  | {
      /**
       * If true, the onChange call back will receive a
       * string instead of a ChangeEvent
       */
      provideValueOnChange: true;
      onChange?: (value: string) => void;
    }
  | {
      provideValueOnChange?: false;
      onChange?: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    };

export type InputRef = { reset: () => void };

export type InputProps = {
  /**
   * Provide a name if you're using the input inside a form
   */
  name?: string;
  /**
   * Custom style class to extend the input
   */
  className?: string;
  containerClassName?: string;
  /**
   * Flag to disable the input
   */
  disabled?: boolean;
  readOnly?: boolean;
  id?: string;
  /**
   * Test id for Cypress and other test suites, maps to `data-testid`
   */
  testId?: string;
  value?: string | number;
  defaultValue?: string | number;
  placeholder?: string;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  maxLength?: number;
  onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  disableUnderLine?: boolean;
  forceErrorNoMessage?: boolean;
  errorMessage?: string;
  errorMessagePosition?: 'top' | 'bottom';
  selectAllOnFocus?: boolean;
  label?: string;
  type?: string;
  maskFunc?: (value?: string) => string;
  ref?: Ref<unknown>;
} & OnChangeProps;

export const Input = ({
  name,
  className,
  containerClassName,
  disabled,
  readOnly,
  id,
  testId,
  value,
  defaultValue = '',
  placeholder,
  onChange,
  provideValueOnChange,
  multiline,
  minRows,
  maxRows,
  maxLength,
  onBlur,
  selectAllOnFocus,
  label,
  type = 'text',
  disableUnderLine = true,
  errorMessage = '',
  errorMessagePosition,
  forceErrorNoMessage,
  maskFunc,
  ref,
}: InputProps) => {
  const [localValue, setLocalValue] = useState(
    typeof defaultValue === 'string' ? `${defaultValue}` : '',
  );

  const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);

    if (provideValueOnChange) {
      onChange?.(newValue);
    } else {
      onChange?.(event);
    }
  };

  const resetForm = useCallback(() => {
    setLocalValue('');
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      reset: resetForm,
    }),
    [resetForm],
  );

  useEffect(() => {
    if (['string', 'number'].includes(typeof value)) {
      setLocalValue(`${value}`);
    }
  }, [value]);

  const errorMessageElement = errorMessage?.length > 0 && (
    <p className={styles.errorText}>
      {errorMessage}{' '}
      <span className={styles.warningIcon}>
        <DangerIcon size={18} />
      </span>
    </p>
  );

  return (
    <div className={classnames(styles.inputContainer, containerClassName)}>
      {label && <MUILabel>{label}</MUILabel>}

      {errorMessagePosition !== 'bottom' && errorMessageElement}
      <MUIInput
        id={id}
        name={name}
        value={!!maskFunc ? maskFunc(localValue) : localValue}
        onChange={handleOnChange}
        onFocus={selectAllOnFocus ? (event) => event.target.select() : undefined}
        className={classnames(styles.input, className, {
          [styles.errorOnInput]: errorMessage.length > 0,
          [styles.multiline]: multiline,
          [styles.greyBackground]: !errorMessage,
        })}
        inputProps={{
          maxLength,
          'aria-label': name,
        }}
        readOnly={readOnly}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testId}
        multiline={multiline}
        minRows={minRows || 21}
        maxRows={maxRows}
        ref={ref}
        onBlur={onBlur}
        error={!!errorMessage || forceErrorNoMessage}
        disableUnderline={disableUnderLine}
        type={type}
      />
      {errorMessagePosition === 'bottom' && errorMessageElement}
    </div>
  );
};
